// src/components/common/Buttons.js
// 77차 신규: design 폴더(design_handoff_unlock, 섹션 05 "버튼 시스템")를 코드로 옮긴
// 공용 버튼/뱃지 세트. Primary는 기존 GradientButton을 그대로 그 역할로 채택(신규 작성 안 함).
// 여기 추가하는 건 나머지 5종: Secondary/Danger/Text/Icon/Badge.
// 각 화면이 제각각 TouchableOpacity+인라인 스타일로 만들던 수정/삭제/D-DAY/플랜뱃지 등을
// 이 컴포넌트들로 교체해 디자인을 통일한다.
import React from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { COLORS, FONTS } from '../../constants/theme';
import { PLAN_COLORS, normalizePlan, getPlanBadgeLabel } from '../../constants';

// 78차 32차: 접근성 라벨 전수 점검 — 4종 전부 accessibilityLabel을 받아 Pressable에
// 전달하도록 함(안 넘기면 label을 그대로 씀, "✕" 같은 기호 label은 스크린리더가
// "곱하기 기호" 등으로 읽어 의미 전달이 안 돼 호출부에서 명시적으로 override 가능하게).
// IconButton은 children이 아이콘뿐이라 라벨이 필수 — 없으면 스크린리더가 완전히 무음.

// 2 · Secondary — 골드 아웃라인 (취소·템플릿 불러오기 등)
// 80차 계속: hitSlop(선택) — 시각적 크기는 작게 유지하면서 실제 터치 영역만 넓히고
// 싶은 호출부(예: CrewScreen.js의 공간 절약형 소형 버튼, fork 재검토로 터치영역이
// 접근성 최소기준 미달임이 발견됨)를 위해 추가 — 기본값 undefined라 기존 호출부는
// 전부 그대로 동작(Pressable은 hitSlop이 undefined면 확장 없이 원래 크기 그대로 씀).
export function SecondaryButton({ label, onPress, disabled = false, style, textStyle, accessibilityLabel, hitSlop }) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      hitSlop={hitSlop}
      accessibilityLabel={accessibilityLabel || label}
      style={({ pressed }) => [
        s.base,
        s.secondaryDefault,
        pressed && !disabled && s.secondaryPressed,
        disabled && s.disabledBorder,
        style,
      ]}
    >
      <Text style={[s.secondaryText, disabled && s.textDisabled, textStyle]}>{label}</Text>
    </Pressable>
  );
}

// 3 · Danger — 삭제·크루 탈퇴·나가기
export function DangerButton({ label, onPress, disabled = false, style, textStyle, accessibilityLabel }) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel || label}
      style={({ pressed }) => [
        s.base,
        s.dangerDefault,
        pressed && !disabled && s.dangerPressed,
        disabled && s.disabledBorder,
        style,
      ]}
    >
      <Text style={[s.dangerText, disabled && s.textDisabled, textStyle]}>{label}</Text>
    </Pressable>
  );
}

// 4 · Text / Link — 뒤로가기·더보기
export function TextButton({ label, onPress, disabled = false, style, textStyle, accessibilityLabel }) {
  return (
    <Pressable onPress={onPress} disabled={disabled} accessibilityLabel={accessibilityLabel || label} style={({ pressed }) => [s.textBtn, style, pressed && !disabled && { opacity: 0.85 }]}>
      {({ pressed }) => (
        <Text style={[s.textBtnText, pressed && !disabled && s.textBtnPressed, disabled && s.textDisabled, textStyle]}>{label}</Text>
      )}
    </Pressable>
  );
}

// 5 · Icon — 좋아요·신고 등 44×44 히트영역. children으로 아이콘(svg 등)을 넘긴다.
// accessibilityLabel 필수(children이 시각적 아이콘뿐이라 안 넘기면 스크린리더가 무음).
export function IconButton({ children, onPress, disabled = false, style, accessibilityLabel }) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      style={({ pressed }) => [
        s.iconBtn,
        pressed && !disabled && s.iconBtnPressed,
        disabled && s.iconBtnDisabled,
        pressed && !disabled && { transform: [{ scale: 0.94 }] },
        style,
      ]}
    >
      {children}
    </Pressable>
  );
}

// 6 · Badge/Chip — D-DAY
export function DDayBadge({ label, urgent = false, style }) {
  return (
    <View style={[s.ddayBase, urgent ? s.ddayUrgent : s.ddayChill, style]}>
      <Text style={[s.ddayText, urgent ? s.ddayTextUrgent : s.ddayTextChill]}>{label}</Text>
    </View>
  );
}

// 6 · Badge/Chip — 플랜(STANDARD/PREMIUM) 컬러 라인 뱃지
// 78차 14차: normalizePlan 적용 — 레거시 'boost' 값(BOOST 폐지 전 데이터)도 premium으로
// 정상 표시되도록(이전엔 정규화 없이 그대로 조회해 'BASIC'으로 잘못 표시될 수 있었음)
// 80차 후속: opsAddon(매장관리자 기능 애드온) prop 추가 — 켜져 있으면 "STANDARD+"처럼 +표시
export function PlanBadge({ plan, opsAddon, style }) {
  const normalized = normalizePlan(plan);
  const color = PLAN_COLORS[normalized];
  return (
    <View style={[s.planBase, style]}>
      <View style={[s.planBar, { backgroundColor: color }]} />
      <Text style={[s.planText, { color }]}>{getPlanBadgeLabel(plan, opsAddon)}</Text>
    </View>
  );
}

// 6 · Badge/Chip — 장르 (getGenreMeta의 color를 그대로 outline에 사용)
export function GenreBadge({ label, color, style }) {
  return (
    <View style={[s.genreBase, { borderColor: color + '66' }, style]}>
      <Text style={[s.genreText, { color }]}>{label}</Text>
    </View>
  );
}

const s = StyleSheet.create({
  base: {
    borderRadius: 12,
    borderWidth: 1,
    paddingVertical: 12,
    paddingHorizontal: 25,
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabledBorder: { borderColor: 'rgba(255,255,255,0.1)' },
  textDisabled: { color: '#4E4E5A' },

  secondaryDefault: { backgroundColor: 'transparent', borderColor: 'rgba(216,174,82,0.55)' },
  secondaryPressed: { backgroundColor: 'rgba(216,174,82,0.12)', borderColor: COLORS.gold },
  secondaryText: { color: COLORS.goldLight, fontSize: 14, fontFamily: FONTS.bodySemiBold },

  dangerDefault: { backgroundColor: 'transparent', borderColor: 'rgba(205,111,102,0.5)' },
  dangerPressed: { backgroundColor: 'rgba(205,111,102,0.16)', borderColor: COLORS.red },
  dangerText: { color: '#E28E86', fontSize: 14, fontFamily: FONTS.bodySemiBold },

  textBtn: { paddingVertical: 8, paddingHorizontal: 4, alignSelf: 'flex-start' },
  textBtnText: { color: '#C8C8D2', fontSize: 14, fontFamily: FONTS.bodyMedium },
  textBtnPressed: { color: COLORS.goldLight },

  iconBtn: {
    width: 44, height: 44, borderRadius: 12,
    backgroundColor: COLORS.surface2, borderWidth: 1, borderColor: COLORS.border,
    alignItems: 'center', justifyContent: 'center',
  },
  iconBtnPressed: { backgroundColor: 'rgba(216,174,82,0.14)', borderColor: 'rgba(216,174,82,0.5)' },
  iconBtnDisabled: { backgroundColor: '#16161f', borderColor: 'rgba(255,255,255,0.05)' },

  ddayBase: { borderRadius: 999, paddingVertical: 5, paddingHorizontal: 11 },
  ddayUrgent: { backgroundColor: COLORS.gold },
  ddayChill: { backgroundColor: 'transparent', borderWidth: 1, borderColor: 'rgba(255,255,255,0.14)' },
  ddayText: { fontSize: 11, fontWeight: '700' },
  ddayTextUrgent: { color: COLORS.onGold },
  ddayTextChill: { color: COLORS.textSec },

  planBase: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    backgroundColor: COLORS.surface2, borderRadius: 8, paddingVertical: 6, paddingHorizontal: 11,
  },
  planBar: { width: 3, height: 12, borderRadius: 2 },
  planText: { fontSize: 11, fontWeight: '700', fontFamily: FONTS.brandSemiBold },

  genreBase: { borderWidth: 1, borderRadius: 6, paddingVertical: 5, paddingHorizontal: 10 },
  genreText: { fontSize: 11 },
});
